import React, { useEffect, useState } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { Tooltip, Button, Center, Text, useToast, Box } from '@chakra-ui/react';
import useRequest from 'src/utils/hooks/useRequest';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import InfoTooltip from 'src/components/atoms/info-tooltip';

interface props {
  isRegister: boolean;
  resetPasswordToken?: string;
  onClose?: () => void;
}

const SSOGoogleSigninButton = ({ isRegister, resetPasswordToken, onClose }: props) => {
  const { api } = useRequest();
  const toast = useToast();
  const { marketplace, hostname } = useMarketplaceContext();
  const [inAppBrowser, setInAppBrowser] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const upIn = isRegister ? 'Sign up' : 'Log in';

  const authOrigin =
    process.env.NEXT_PUBLIC_ENV === 'staging'
      ? 'https://auth.staging.withloma.com'
      : process.env.NEXT_PUBLIC_ENV === 'development'
      ? 'http://loma-marketplace.localhost:3030'
      : 'https://auth.withloma.com';

  useEffect(() => {
    const isInAppBrowser = () => {
      if (typeof window === 'undefined' || !('userAgent' in window.navigator)) return false;
      const ua = window.navigator.userAgent;
      if (!ua) return false;
      return ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1 || ua.indexOf('Instagram') > -1;
    };

    if (isInAppBrowser()) {
      setInAppBrowser(true);
    }
  }, []);

  const handleSignIn = async (event: any) => {
    event.preventDefault();

    if (!marketplace) return;

    // generate the ticket token, which will be usable for 10 minutes
    const redirectUri = `https://${hostname}/validate-ticket`;
    const payload = resetPasswordToken ? { reset_password_token: resetPasswordToken } : {};
    const ticketToken = await api.marketplace.sso.generateTicket(
      marketplace.id,
      redirectUri,
      payload,
    );
    if (!ticketToken) {
      toast({
        title: 'Error',
        description: 'Social login currently unavailable. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error('No ticket token generated');
      return;
    }
    /*
    Login flow:
    - Store the ticket token in local storage on the marketplace.
    - Redirect the user to https://auth.withloma.com/oauth-trigger
    - From there, store the ticket in the cookie on auth.withloma.com and trigger the NextAuth signIn flow.
    - User attempts login and is sent to the callback defined in src/pages/api/auth/[...nextauth].ts
    - The callback passes username/email payload to the backend (via sso/token/social/), where it is stored in the SSOLoginTicket record.
    - User is redirect from the auth server to https://{marketplace_domain}/validate-ticket
    - Ticket is loaded from local storage and the login is attempted on the backend using the ticket.
    - If valid, user is logged in as normal (access/refresh tokens are set).
    */
    localStorage.setItem('ticket', ticketToken);

    if (inAppBrowser) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 5000);
    } else {
      onClose?.();

      // send the user to the auth server and trigger the signIn there
      window.location.href = `${authOrigin}/oauth-trigger?ticket=${encodeURIComponent(
        ticketToken,
      )}&name=${encodeURIComponent(marketplace.name)}`;
    }
  };

  return (
    <>
      <Center px={8} pb={4}>
        <Tooltip
          label={`To ${upIn.toLowerCase()} with Google, please open this link in your dedicated mobile browser.`}
          isOpen={showTooltip}
        >
          <Button
            onClick={handleSignIn}
            w={'full'}
            maxW={'md'}
            variant={'outline'}
            leftIcon={<FcGoogle />}
            data-test="google-signup-btn"
            bgColor="#f9f9f9"
            isDisabled={!marketplace}
          >
            <Center>
              <Text>{`${upIn} with Google`}</Text>
            </Center>
          </Button>
        </Tooltip>
      </Center>

      <Box textAlign="center" px={8} pb={8}>
        <Text fontSize="xs" color="gray.600">
          Powered by <b>Loma</b>
          <InfoTooltip
            description="We use Loma for our commerce platform and user verification."
            boxSize={3}
          />
        </Text>
      </Box>
    </>
  );
};

export default SSOGoogleSigninButton;
